import { useEffect, useMemo } from "react";

import { Loader } from "@jti/core-components/components/Loader";
import { isServerSide } from "@jti/fe-common";
import { HeroV2 } from "@jti/ui/components/HeroV2";
import type { HeroV2CtaType } from "@jti/ui/components/HeroV2/types/HeroV2Types";
import { useTrialStatus } from "@jti/ui/hooks/useTrialStatus/useTrialStatus";
import { useUser } from "@jti/ui/hooks/useUser/useUser";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";
import { useTrialEligibility } from "@jti/ui/providers/TrialEligibilityContext";

import "./HeroBanner.scss";

export interface HeroComponentPropsType {
  tabletImg?: string;
  mobileImg: string;
  img: string;
  heading: string;
  loggedAvailableTitle: string;
  loggedFinishedTitle: string;
  unloggedTitle: string;
  availableDescription: string;
  finishedDescription: string;
  actionButtonAvailableTitle?: string;
  actionButtonAvailableUrl?: string;
  actionButtonFinishedTitle?: string;
  actionButtonFinishedUrl?: string;
  actionButtonLoggedOutTitle?: string;
  actionButton2AvailableTitle?: string;
  actionButton2AvailableUrl?: string;
  actionButton2FinishedTitle?: string;
  actionButton2FinishedUrl?: string;
  actionButton2LoggedOutTitle?: string;
  actionButton2LoggedOutUrl?: string;
  signInUrl: string;
  breadcrumbsSpacing?: boolean;
}
const HeroBanner = ({
  tabletImg,
  mobileImg,
  img,
  heading,
  loggedAvailableTitle,
  loggedFinishedTitle,
  unloggedTitle,
  availableDescription,
  finishedDescription,
  actionButtonAvailableTitle,
  actionButtonAvailableUrl,
  actionButtonFinishedTitle = "",
  actionButtonFinishedUrl,
  actionButtonLoggedOutTitle,
  actionButton2AvailableTitle,
  actionButton2AvailableUrl,
  actionButton2FinishedTitle = "",
  actionButton2FinishedUrl,
  actionButton2LoggedOutTitle,
  actionButton2LoggedOutUrl,
  signInUrl,
  breadcrumbsSpacing = false,
}: HeroComponentPropsType) => {
  const trialEligibility = useTrialEligibility();
  const resolveAuthHref = useAuthHrefResolver();

  // TODO: replace DOM operations with message bus communication (https://jira.jtiweb.co.uk/browse/DESP-36561)
  const productTeaserTryNowCta = useMemo(
    () => (isServerSide() ? [] : document.querySelectorAll(".product-teaser-try-now__wrapper")),
    []
  );
  const uniqueCategoryIds = useMemo(() => {
    const uniq = Array.from(productTeaserTryNowCta).reduce<Set<string>>((acc, cta) => {
      if (!(cta instanceof HTMLElement)) {
        return acc;
      }

      try {
        const categories = JSON.parse(cta.dataset.categories || "[]") as Array<{ id: string; name: string }>;
        if (categories.length === 0 || !categories[0]?.id) {
          return acc;
        }

        const firstCategoryId = categories[0].id;
        return acc.add(firstCategoryId); // As multiple categories is a misconfiguration, we are only checking the first one
      } catch {
        return acc;
      }
    }, new Set<string>());
    return { categoryIds: Array.from(uniq) };
  }, [productTeaserTryNowCta]);

  // Mixing two worlds, AEM and nextjs. `useTrialStatus` is needed in AEM, in nextjs we have the info from context exposed with useTrialEligibility
  // TODO only needed while AEM trial landing page is live
  const { isLoading: isTrialStatusLoading, eligibleCategoryIds, isTrialAvailable } = useTrialStatus(uniqueCategoryIds);
  const isLoadingTrialEligibility = trialEligibility ? trialEligibility.isLoading : isTrialStatusLoading;
  const canParticipateInTrial = trialEligibility ? trialEligibility.isTrialAvailable : isTrialAvailable;

  const { isUserLoggedIn } = useUser();
  const actionButtonUrl = canParticipateInTrial ? actionButtonAvailableUrl : actionButtonFinishedUrl;
  const actionButtonTitle = canParticipateInTrial ? actionButtonAvailableTitle : actionButtonFinishedTitle;
  const actionButton2Url = canParticipateInTrial ? actionButton2AvailableUrl : actionButton2FinishedUrl;
  const actionButton2Title = canParticipateInTrial ? actionButton2AvailableTitle : actionButton2FinishedTitle;

  const ctas: HeroV2CtaType[] = useMemo(() => {
    if (isServerSide()) {
      return [];
    }

    const ctas: HeroV2CtaType[] = [];
    const freeTrialPathname = window.location.pathname;
    if (actionButtonLoggedOutTitle) {
      const search = { returnUrl: freeTrialPathname };

      ctas.push({
        link: resolveAuthHref({ predefinedHref: signInUrl, search }),
        additionalBehavior: "onlyForNotLoggedIn",
        label: actionButtonLoggedOutTitle,
        theme: "dark",
        layout: "full",
        tracking: "",
        target: "",
      });
    }
    if (actionButton2LoggedOutTitle && actionButton2LoggedOutUrl) {
      ctas.push({
        link: actionButton2LoggedOutUrl,
        additionalBehavior: "onlyForNotLoggedIn",
        label: actionButton2LoggedOutTitle,
        theme: "light",
        layout: "full",
        tracking: "",
        target: "",
      });
    }
    if (actionButtonUrl && actionButtonTitle) {
      ctas.push({
        link: actionButtonUrl,
        additionalBehavior: "onlyForLoggedIn",
        label: actionButtonTitle,
        theme: "dark",
        layout: "full",
        tracking: "",
        target: "",
      });
    }
    if (actionButton2Title && actionButton2Url) {
      ctas.push({
        link: actionButton2Url,
        additionalBehavior: "onlyForLoggedIn",
        label: actionButton2Title,
        theme: "light",
        layout: "full",
        tracking: "",
        target: "",
      });
    }
    return ctas;
  }, [
    actionButtonLoggedOutTitle,
    actionButton2LoggedOutTitle,
    actionButton2LoggedOutUrl,
    actionButtonUrl,
    actionButtonTitle,
    actionButton2Title,
    actionButton2Url,
    signInUrl,
    resolveAuthHref,
  ]);

  // Hide try now buttons for non-eligible products
  // TODO only needed while AEM trial landing page is live
  useEffect(() => {
    const checkEligibility = ({ button, eligibleCategoryIds }: { button: Element; eligibleCategoryIds: string[] }) => {
      if (!(button instanceof HTMLElement)) {
        return false;
      }

      try {
        const categories = JSON.parse(button.dataset.categories || "[]") as Array<{ id: string; name: string }>;
        if (categories.length === 0 || !categories[0]?.id) {
          return false;
        }

        const firstCategoryId = categories[0].id;
        return eligibleCategoryIds.includes(firstCategoryId);
      } catch {
        return false;
      }
    };

    productTeaserTryNowCta.forEach((button) => {
      const isEligible = !isTrialStatusLoading && checkEligibility({ button, eligibleCategoryIds });

      button.classList.toggle("hide", !isEligible);
      button
        .closest(".product-teaser-component__base")
        ?.classList.toggle("product-teaser-component__base--expanded", isEligible);
    });
  }, [isUserLoggedIn, eligibleCategoryIds, productTeaserTryNowCta, isTrialStatusLoading]);

  if (isLoadingTrialEligibility) {
    return (
      <div className={"hero__loader"}>
        <Loader />
      </div>
    );
  }

  return (
    <HeroV2
      data={[
        {
          backgroundType: "image",
          mobileVariant: "transparentContentOnTop",
          desktopImage: {
            src: img,
            title: "",
            alt: "",
          },
          mediumImage: {
            src: tabletImg || img,
            title: "",
            alt: "",
          },
          smallImage: {
            src: mobileImg,
            title: "",
            alt: "",
          },
          videoFileReference: "",
          tabletVideoFileReference: "",
          mobileVideoFileReference: "",
          displayScrollBar: false,
          legalDisclaimer: "",
          logo: "",
          logoReference: "PLOOM_X",
          clipped: false,
          withBreadcrumbs: breadcrumbsSpacing,
          content: {
            visibility: "visible",
            tagLine: heading,
            title: unloggedTitle,
            titleLogged: canParticipateInTrial ? loggedAvailableTitle : loggedFinishedTitle,
            subheadline: "",
            subheadlineLogged: "",
            text: availableDescription,
            textLogged: canParticipateInTrial ? availableDescription : finishedDescription,
            cta: ctas,
            discounts: [],
          },
        },
      ]}
    />
  );
};

export default HeroBanner;
